import React, { useEffect, useRef, useState } from "react";
import { fireAlert } from "../../../utils/utils";
import { formatFileSize } from "../../../utils/whatsappMessages";

/**
 * Attachments toolbar for the chat composer:
 * image, video, document, audio (file), audio (MediaRecorder), location,
 * contact, sticker. It only builds the attachment object; the composer keeps
 * the state and sends it through the existing POST chat/send contract.
 */
const ATTACHMENT_ICON = {
  image: "\uD83D\uDDBC\uFE0F",
  video: "\uD83C\uDFA5",
  audio: "\uD83C\uDFB5",
  document: "\uD83D\uDCC4",
  sticker: "\uD83C\uDFF7\uFE0F",
  location: "\uD83D\uDCCD",
  contacts: "\uD83D\uDC64",
  reaction: "\u2764\uFE0F"
};

function attachmentLabel(attachment) {
  if (!attachment) {
    return "";
  }

  if (attachment.msgtype === "location") {
    return "Location " + attachment.latitude + ", " + attachment.longitude;
  }

  if (attachment.msgtype === "contacts") {
    const contact = attachment.payload?.contacts?.[0] || {};
    return contact.name || contact.phone || "Contact";
  }

  const name = attachment.filename || (attachment.msgtype === "audio" && attachment.payload?.voice ? "Voice message" : attachment.msgtype);
  return attachment.size ? name + " - " + formatFileSize(attachment.size) : name;
}

function ToolbarButton({ title, icon, onClick, disabled, active }) {
  return (
    <span
      className="btn btn-icon p-0 me-2 align-self-center"
      title={title}
      onClick={disabled ? undefined : onClick}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 16,
        lineHeight: 1,
        opacity: disabled ? 0.4 : 1,
        color: active ? "#640180" : "#6e747c"
      }}
    >
      {icon}
    </span>
  );
}

export default function ChatAttachments({ attachment, setAttachment, disabled }) {
  const imageRef = useRef();
  const videoRef = useRef();
  const documentRef = useRef();
  const audioRef = useRef();
  const stickerRef = useRef();
  const recorderRef = useRef();
  const opusRecorderRef = useRef(null);
  const opusChunksRef = useRef([]);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  useEffect(() => {
    return function cleanup() {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      recorderRef.current = null;
    };
  }, []);

  function onFileChange(event, msgtype) {
    const file = event?.target?.files?.[0];

    if (!file) {
      return;
    }

    setAttachment({
      msgtype,
      file,
      filename: file.name,
      size: file.size,
      previewUrl:
        msgtype === "image" || msgtype === "sticker"
          ? (window.URL || window.webkitURL).createObjectURL(file)
          : undefined
    });

    event.target.value = "";
  }

  function opusAsset(path) {
    return (process.env.PUBLIC_URL || "") + path;
  }

  function loadOpusRecorder() {
    if (typeof window === "undefined") {
      return Promise.reject(new Error("sem window"));
    }

    if (window.Recorder) {
      return Promise.resolve(window.Recorder);
    }

    return new Promise(function (resolve, reject) {
      const existing = document.getElementById("opus-recorder-script");

      function done() {
        if (window.Recorder) {
          resolve(window.Recorder);
        } else {
          reject(new Error("Recorder nao disponivel"));
        }
      }

      if (existing) {
        existing.addEventListener("load", done);
        existing.addEventListener("error", function () { reject(new Error("script do gravador falhou")); });
        return;
      }

      const script = document.createElement("script");
      script.id = "opus-recorder-script";
      script.async = true;
      script.src = opusAsset("/opus-recorder/recorder.min.js");
      script.onload = done;
      script.onerror = function () { reject(new Error("nao foi possivel carregar o gravador ogg/opus")); };
      document.body.appendChild(script);
    });
  }

  // O WhatsApp nao aceita webm e recusa o MP4 fragmentado do MediaRecorder
  // ("processed as application/octet-stream"). Este gravador Ogg/Opus roda em
  // WASM no proprio navegador e gera o formato aceito: audio/ogg; codecs=opus.
  function startOpusRecording(Recorder) {
    const recorder = new Recorder({
      encoderPath: opusAsset("/opus-recorder/encoderWorker.min.js"),
      numberOfChannels: 1,
      encoderSampleRate: 48000,
      encoderApplication: 2048,
      streamPages: false,
      mediaTrackConstraints: { audio: true }
    });

    opusChunksRef.current = [];

    recorder.ondataavailable = function (data) {
      if (data && data.length) {
        opusChunksRef.current.push(new Uint8Array(data));
      }
    };

    recorder.onstart = function () {
      setRecording(true);
    };

    recorder.onstop = function () {
      const blob = new Blob(opusChunksRef.current, { type: "audio/ogg" });
      const file = new File([blob], "voice-" + Date.now() + ".ogg", { type: "audio/ogg" });

      setAttachment({
        msgtype: "audio",
        file,
        filename: file.name,
        size: file.size,
        payload: { voice: true }
      });

      setRecording(false);
    };

    opusRecorderRef.current = recorder;

    return recorder.start().catch(function (error) {
      opusRecorderRef.current = null;
      setRecording(false);
      throw error;
    });
  }

  function startRecording() {
    loadOpusRecorder()
      .then(function (Recorder) {
        return startOpusRecording(Recorder);
      })
      .catch(function (error) {
        console.log("Gravador ogg/opus indisponivel, usando MediaRecorder:", error && error.message);
        startMediaRecorderRecording();
      });
  }

  function startMediaRecorderRecording() {
    if (typeof navigator === "undefined" || !navigator.mediaDevices || typeof MediaRecorder === "undefined") {
      fireAlert("Audio recording is not available in this browser");
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(function (stream) {
        // Ordem por compatibilidade: mp4/aac e aceito pelo WhatsApp e toca em qualquer
        // navegador (inclusive Safari/iOS); ogg/opus e o formato de nota de voz;
        // webm/opus e o fallback do Chrome que o backend converte para ogg.
        const supportedTypes = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"];
        const preferredType = supportedTypes.find(function (type) {
          return typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(type);
        });

        const recorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined);
        chunksRef.current = [];

        recorder.ondataavailable = function (dataEvent) {
          if (dataEvent.data && dataEvent.data.size > 0) {
            chunksRef.current.push(dataEvent.data);
          }
        };

        recorder.onstop = function () {
          stream.getTracks().forEach(function (track) {
            track.stop();
          });

          const type = recorder.mimeType || "audio/webm";
          const extension = type.indexOf("ogg") >= 0 ? "ogg" : type.indexOf("mp4") >= 0 ? "m4a" : "webm";
          const blob = new Blob(chunksRef.current, { type });
          const file = new File([blob], "voice-" + Date.now() + "." + extension, { type });

          setAttachment({
            msgtype: "audio",
            file,
            filename: file.name,
            size: file.size,
            payload: { voice: true }
          });
        };

        recorderRef.current = recorder;
        recorder.start();
        setRecording(true);
      })
      .catch(function () {
        fireAlert("Microphone permission denied");
        setRecording(false);
      });
  }

  function stopRecording() {
    const opusRecorder = opusRecorderRef.current;

    if (opusRecorder) {
      opusRecorder.stop();
      opusRecorderRef.current = null;
      return;
    }

    const recorder = recorderRef.current;

    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }

    recorderRef.current = null;
    setRecording(false);
  }

  function handleLocation() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      fireAlert("Location is not available in this browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      function (position) {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        setAttachment({
          msgtype: "location",
          latitude,
          longitude,
          payload: { location: { latitude, longitude } }
        });
      },
      function () {
        fireAlert("Could not get your current location");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  function handleContact() {
    const name = contactName.trim();
    const phone = contactPhone.trim();

    if (!name && !phone) {
      return;
    }

    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      "FN:" + name,
      "TEL;type=CELL:" + phone,
      "END:VCARD"
    ].join("\n");

    setAttachment({
      msgtype: "contacts",
      payload: { contacts: [vcard] }
    });
    setShowContact(false);
    setContactName("");
    setContactPhone("");
  }

  return (
    <>
      <span className="d-flex flex-row align-items-center me-2">
        <ToolbarButton title="Image" icon={ATTACHMENT_ICON.image} disabled={disabled} onClick={() => imageRef.current?.click()} />
        <ToolbarButton title="Video" icon={ATTACHMENT_ICON.video} disabled={disabled} onClick={() => videoRef.current?.click()} />
        <ToolbarButton title="Document" icon={ATTACHMENT_ICON.document} disabled={disabled} onClick={() => documentRef.current?.click()} />
        <ToolbarButton title="Audio file" icon={ATTACHMENT_ICON.audio} disabled={disabled} onClick={() => audioRef.current?.click()} />
        <ToolbarButton
          title={recording ? "Stop recording" : "Record voice message"}
          icon={recording ? "\u23F9" : "\uD83C\uDF99"}
          disabled={disabled}
          active={recording}
          onClick={() => (recording ? stopRecording() : startRecording())}
        />
        <ToolbarButton title="Location" icon={ATTACHMENT_ICON.location} disabled={disabled} onClick={handleLocation} />
        <ToolbarButton title="Contact" icon={ATTACHMENT_ICON.contacts} disabled={disabled} active={showContact} onClick={() => setShowContact(!showContact)} />
        <ToolbarButton title="Sticker" icon={ATTACHMENT_ICON.sticker} disabled={disabled} onClick={() => stickerRef.current?.click()} />

        <input ref={imageRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(event) => onFileChange(event, "image")} />
        <input ref={videoRef} type="file" accept="video/*" style={{ display: "none" }} onChange={(event) => onFileChange(event, "video")} />
        <input ref={documentRef} type="file" style={{ display: "none" }} onChange={(event) => onFileChange(event, "document")} />
        <input ref={audioRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={(event) => onFileChange(event, "audio")} />
        <input ref={stickerRef} type="file" accept="image/webp,image/png,image/jpeg" style={{ display: "none" }} onChange={(event) => onFileChange(event, "sticker")} />
      </span>

      {showContact && (
        <span className="d-flex flex-row align-items-center me-2" style={{ fontSize: 11 }}>
          <input
            className="form-control form-control-sm me-1"
            style={{ width: 120 }}
            placeholder="Name"
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
          />
          <input
            className="form-control form-control-sm me-1"
            style={{ width: 130 }}
            placeholder="Phone"
            value={contactPhone}
            onChange={(event) => setContactPhone(event.target.value)}
          />
          <span className="btn btn-sm btn-link p-0" style={{ cursor: "pointer", color: "#640180" }} onClick={handleContact}>
            Add
          </span>
        </span>
      )}

      {attachment && (
        <span
          className="d-flex flex-row align-items-center rounded-3 px-2 py-1 me-2"
          style={{ backgroundColor: "#F6F9FB", border: "1px solid #e7e7e7", fontSize: 11, maxWidth: 260 }}
        >
          <span className="me-1">{ATTACHMENT_ICON[attachment.msgtype] || "\uD83D\uDCCE"}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachmentLabel(attachment)}</span>
          <span
            className="ms-2"
            title="Remove attachment"
            style={{ cursor: "pointer", color: "#6e747c" }}
            onClick={() => setAttachment(undefined)}
          >
            \u00D7
          </span>
        </span>
      )}
    </>
  );
}
